import { Injectable } from '@angular/core';

import { environment } from 'environments/environment';
import { CookieService } from 'ngx-cookie-service';

@Injectable()
export class VerifiedMobileService {
  private readonly COOKIE_KEY = '__ad_vmn__';
  private readonly SECRET = environment.encryptionSecretKey;

  constructor(private cookieService: CookieService) {}

  public async getVerifiedNumbers(): Promise<string[]> {
    const hasVerifiedPhoneNumbers = this.cookieService.check(this.COOKIE_KEY);

    if (!hasVerifiedPhoneNumbers) {
      return [];
    }
    const encryptedValue = this.cookieService.get(this.COOKIE_KEY);
    try {
      return await this.decryptValue(encryptedValue);
    } catch (e) {
      console.log('decryption failed', e);
      return [];
    }
  }

  public async isVerified(mobile: string): Promise<boolean> {
    const verifiedNumbers = await this.getVerifiedNumbers();
    return verifiedNumbers.includes(mobile);
  }

  public async addVerifiedNumber(
    mobile: string,
    expirationTime: Date
  ): Promise<void> {
    const verifiedNumbers = await this.getVerifiedNumbers();
    verifiedNumbers.push(mobile);

    const uniqueVerifiedNumbers = Array.from(new Set(verifiedNumbers));
    const encryptedValue = await this.encryptValue(uniqueVerifiedNumbers);

    this.cookieService.set(
      this.COOKIE_KEY,
      encryptedValue,
      expirationTime,
      '/'
    );
  }

  private async getEncryptionKey(): Promise<CryptoKey> {
    const enc = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      enc.encode(this.SECRET),
      'PBKDF2',
      false,
      ['deriveKey']
    );
    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: enc.encode(this.SECRET),
        iterations: 100000,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );
  }

  private async encryptValue(data: string[]): Promise<string> {
    const key = await this.getEncryptionKey();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encoded = new TextEncoder().encode(JSON.stringify(data));
    const ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      encoded
    );

    const combined = new Uint8Array(iv.length + ciphertext.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(ciphertext), iv.length);
    return btoa(String.fromCharCode(...combined));
  }

  private async decryptValue(stored: string): Promise<string[]> {
    const key = await this.getEncryptionKey();
    const combined = Uint8Array.from(atob(stored), c => c.charCodeAt(0));
    const iv = combined.slice(0, 12);
    const ciphertext = combined.slice(12);
    const plainBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      ciphertext
    );
    return JSON.parse(new TextDecoder().decode(plainBuffer));
  }
}
